import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useGame } from '../GameContext'
import { findHole } from '../data'
import { Button, WashiTape } from '../components/ui'
import { ScorecardTable } from '../components/Scorecard'
import { BookOpen, ChevronLeft, ChevronRight, Flag, Trophy } from 'lucide-react'

export default function Play() {
  const navigate = useNavigate()
  const { gameState, updateScore, setCurrentHole } = useGame()
  const [scores, setScores] = useState({})

  useEffect(() => {
    if (!gameState.course || gameState.players.length === 0) navigate('/')
  }, [gameState.course, gameState.players.length, navigate])

  if (!gameState.course || gameState.players.length === 0) return null

  const hole = gameState.holes[gameState.currentHole - 1]
  const holeInfo = findHole(gameState.course.id, gameState.currentHole)

  useEffect(() => {
    if (hole) {
      const next = {}
      hole.scores.forEach((s) => {
        next[s.playerId] = s.strokes
      })
      setScores(next)
    }
  }, [hole, gameState.currentHole])

  const setScore = (playerId, value) => {
    const num = parseInt(value, 10)
    if (!isNaN(num) && num >= 0 && num <= 8) {
      setScores((prev) => ({ ...prev, [playerId]: num }))
      updateScore(gameState.currentHole, playerId, num)
    } else if (value === '') {
      setScores((prev) => ({ ...prev, [playerId]: 0 }))
    }
  }

  const prevHole = () => {
    if (gameState.currentHole > 1) setCurrentHole(gameState.currentHole - 1)
  }

  const nextHole = () => {
    if (gameState.course && gameState.currentHole < gameState.course.holes) {
      setCurrentHole(gameState.currentHole + 1)
    } else {
      navigate('/summary')
    }
  }

  const isLast = gameState.currentHole === gameState.course.holes
  const allFilled = gameState.players.every((p) => scores[p.id] > 0)

  return (
    <div className="min-h-screen flex flex-col py-6">
      <div className="flex-1 max-w-md w-full mx-auto space-y-6 pb-4">
        <div className="flex items-center justify-between relative">
          <div className="flex-1" />
          <div className="grow text-center">
            <WashiTape className="left-1/2 -translate-x-1/2 -top-3" rotation="-3deg" />
            <h1 className="text-2xl font-hand relative z-10">Hole {gameState.currentHole}</h1>
            {holeInfo && (
              <p className="text-sm text-muted-foreground">
                Par {holeInfo.par} | {holeInfo.distance} meter
              </p>
            )}
          </div>
          <div className="flex-1 flex align-center justify-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => navigate('/rules')}>
              <BookOpen className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/standings')}>
              <Trophy className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-center py-2">
          {holeInfo && (
            <img
              src={holeInfo.image}
              alt={`Hole ${holeInfo.number} illustration`}
              className="w-full h-44 object-contain"
            />
          )}
        </div>

        <div className="space-y-6">
          <ScorecardTable
            players={gameState.players}
            holes={gameState.holes}
            currentHole={gameState.currentHole}
            editable
            scores={scores}
            onScoreChange={setScore}
          />
        </div>

        <div className="flex gap-3">
          <Button
            onClick={prevHole}
            disabled={gameState.currentHole === 1}
            variant="outline"
            className="flex-1 bg-card"
            size="lg"
          >
            <ChevronLeft className="mr-2 h-5 w-5" />
            Vorige
          </Button>
          <Button onClick={nextHole} disabled={!allFilled} className="flex-1" size="lg">
            {isLast ? (
              <>
                <Flag className="mr-2 h-5 w-5" />
                Voltooien
              </>
            ) : (
              <>
                Volgende
                <ChevronRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </div>

        <div className="h-24 bg-card border border-border/40 rounded-lg flex items-center justify-center text-xs text-muted-foreground">
          Advertentie ruimte
        </div>

        <div className="bg-card rounded-lg space-y-1 shadow-sm border border-border/40 p-1">
          <div className="flex align-center text-xs">
            <span>
              {gameState.currentHole} / {gameState.course.holes}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all"
              style={{
                width: `${(gameState.currentHole / gameState.course.holes) * 100}%`,
                backgroundColor: gameState.course.color,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}